import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const PublicGroupBooking = () => {
    const { publicId } = useParams();
    const [groupBooking, setGroupBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showJoinForm, setShowJoinForm] = useState(false);
    const [joinFormData, setJoinFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        package_selected: '',
        special_requests: '',
        additional_guests: []
    });
    const [joinLoading, setJoinLoading] = useState(false);

    useEffect(() => {
        fetchGroupBooking();
    }, [publicId]);

    useEffect(() => {
        // Auto-select package if there's only one
        if (groupBooking && groupBooking.packages && groupBooking.packages.length === 1) {
            setJoinFormData(prev => ({
                ...prev,
                package_selected: groupBooking.packages[0].name
            }));
        }
    }, [groupBooking]);

    const fetchGroupBooking = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/group-bookings/public/${publicId}`);
            setGroupBooking(response.data.data);
        } catch (error) {
            console.error('Error fetching group booking:', error);
            setError(error.response?.data?.message || 'Failed to load group booking');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getComponentTypeIcon = (type) => {
        switch (type) {
            case 'cruise': return '🚢';
            case 'flight': return '✈️';
            case 'hotel': return '🏨';
            case 'tour': return '🗺️';
            default: return '🎯';
        }
    };

    const handleJoinFormChange = (e) => {
        const { name, value } = e.target;
        setJoinFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const addGuest = () => {
        setJoinFormData(prev => ({
            ...prev,
            additional_guests: [...prev.additional_guests, { name: '', age: '' }]
        }));
    };

    const removeGuest = (index) => {
        setJoinFormData(prev => ({
            ...prev,
            additional_guests: prev.additional_guests.filter((_, i) => i !== index)
        }));
    };

    const updateGuest = (index, field, value) => {
        setJoinFormData(prev => ({
            ...prev,
            additional_guests: prev.additional_guests.map((guest, i) => 
                i === index ? { ...guest, [field]: value } : guest
            )
        }));
    };

    const handleJoinSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (!joinFormData.first_name || !joinFormData.last_name || !joinFormData.email || !joinFormData.phone || !joinFormData.address || !joinFormData.package_selected) {
            alert('Please fill in all required fields: First Name, Last Name, Email, Phone, Address, and Package Selection');
            return;
        }

        // Validate additional guests
        for (let i = 0; i < joinFormData.additional_guests.length; i++) {
            const guest = joinFormData.additional_guests[i];
            if (!guest.name.trim()) {
                alert(`Please enter a name for Guest ${i + 1} or remove them from the list.`);
                return;
            }
        }

        setJoinLoading(true);
        
        try {
            const response = await axios.post(`/api/group-bookings/public/${publicId}/join`, joinFormData);
            
            if (response.data.success) {
                // Redirect to payment page (same as quote payments)
                window.location.href = response.data.payment_url;
            } else {
                alert(response.data.message || 'Failed to join group booking');
            }
        } catch (error) {
            console.error('Error joining group:', error);
            alert(error.response?.data?.message || 'Failed to join group booking. Please try again.');
        } finally {
            setJoinLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading group booking details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-8">
                    <div className="text-6xl mb-4">❌</div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Group Booking Not Found</h1>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <a 
                        href="https://tpcrm.io" 
                        className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Go to Homepage
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-6xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="text-2xl">{getComponentTypeIcon(groupBooking.component_type)}</div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">{groupBooking.title}</h1>
                                <p className="text-gray-600">Organized by {groupBooking.agency_name}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-sm text-gray-500">Group Travel</div>
                            <div className="text-lg font-semibold text-blue-600">
                                {groupBooking.current_participants || 0} / {groupBooking.max_participants} participants
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Trip Overview */}
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Trip Overview</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <div className="text-sm text-gray-500">Destination</div>
                                    <div className="text-lg font-medium text-gray-900">{groupBooking.destination}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">Travel Type</div>
                                    <div className="text-lg font-medium text-gray-900 capitalize">
                                        {groupBooking.component_type.replace('_', ' ')}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">Start Date</div>
                                    <div className="text-lg font-medium text-gray-900">{formatDate(groupBooking.start_date)}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">End Date</div>
                                    <div className="text-lg font-medium text-gray-900">{formatDate(groupBooking.end_date)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Packages */}
                        {groupBooking.packages && groupBooking.packages.length > 0 && (
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Packages</h2>
                                <div className="space-y-4">
                                    {groupBooking.packages.map((pkg, index) => (
                                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h3 className="font-medium text-gray-900">{pkg.name}</h3>
                                                    <p className="text-gray-600 text-sm mt-1">{pkg.description}</p>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-lg font-semibold text-green-600">
                                                        ${pkg.price}
                                                    </div>
                                                    <div className="text-sm text-gray-500">per person</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Cancellation Policy */}
                        {groupBooking.cancellation_policy && (
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <h2 className="text-xl font-semibold text-gray-900 mb-4">Cancellation Policy</h2>
                                <p className="text-gray-700 whitespace-pre-wrap">{groupBooking.cancellation_policy}</p>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Join Group CTA */}
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Join This Group</h3>
                            <div className="space-y-4">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">
                                            {groupBooking.current_participants || 0}
                                        </div>
                                        <div className="text-sm text-gray-600">of {groupBooking.max_participants} spots filled</div>
                                    </div>
                                </div>
                                
                                <button 
                                    onClick={() => setShowJoinForm(true)}
                                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                >
                                    Join This Group
                                </button>
                                
                                <p className="text-xs text-gray-500 text-center">
                                    By joining, you'll be connected with the travel agent to complete your booking
                                </p>
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Travel Agent</h3>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm text-gray-500">Agent</div>
                                    <div className="font-medium text-gray-900">{groupBooking.agent_contact.name}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">Email</div>
                                    <a 
                                        href={`mailto:${groupBooking.agent_contact.email}`}
                                        className="text-blue-600 hover:text-blue-700"
                                    >
                                        {groupBooking.agent_contact.email}
                                    </a>
                                </div>
                                {groupBooking.agent_contact.phone && (
                                    <div>
                                        <div className="text-sm text-gray-500">Phone</div>
                                        <a 
                                            href={`tel:${groupBooking.agent_contact.phone}`}
                                            className="text-blue-600 hover:text-blue-700"
                                        >
                                            {groupBooking.agent_contact.phone}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Coordinator Info */}
                        {groupBooking.coordinator && groupBooking.coordinator.name && (
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Group Coordinator</h3>
                                <div className="space-y-3">
                                    <div>
                                        <div className="text-sm text-gray-500">Name</div>
                                        <div className="font-medium text-gray-900">{groupBooking.coordinator.name}</div>
                                    </div>
                                    {groupBooking.coordinator.email && (
                                        <div>
                                            <div className="text-sm text-gray-500">Email</div>
                                            <a 
                                                href={`mailto:${groupBooking.coordinator.email}`}
                                                className="text-blue-600 hover:text-blue-700"
                                            >
                                                {groupBooking.coordinator.email}
                                            </a>
                                        </div>
                                    )}
                                    {groupBooking.coordinator.phone && (
                                        <div>
                                            <div className="text-sm text-gray-500">Phone</div>
                                            <a 
                                                href={`tel:${groupBooking.coordinator.phone}`}
                                                className="text-blue-600 hover:text-blue-700"
                                            >
                                                {groupBooking.coordinator.phone}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Join Form Modal */}
            {showJoinForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-semibold text-gray-900">Join Group: {groupBooking.title}</h3>
                                <button 
                                    onClick={() => setShowJoinForm(false)}
                                    className="text-gray-400 hover:text-gray-600 text-2xl"
                                >
                                    ✕
                                </button>
                            </div>
                            
                            <form onSubmit={handleJoinSubmit} className="space-y-6">
                                {/* Personal Information */}
                                <div>
                                    <h4 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                First Name *
                                            </label>
                                            <input
                                                type="text"
                                                name="first_name"
                                                value={joinFormData.first_name}
                                                onChange={handleJoinFormChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Last Name *
                                            </label>
                                            <input
                                                type="text"
                                                name="last_name"
                                                value={joinFormData.last_name}
                                                onChange={handleJoinFormChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Email Address *
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={joinFormData.email}
                                                onChange={handleJoinFormChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Phone Number *
                                            </label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={joinFormData.phone}
                                                onChange={handleJoinFormChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Address *
                                            </label>
                                            <input
                                                type="text"
                                                name="address"
                                                value={joinFormData.address}
                                                onChange={handleJoinFormChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Street address, City, State, ZIP"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Guests */}
                                <div>
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="text-lg font-medium text-gray-900">Additional Guests</h4>
                                        <button
                                            type="button"
                                            onClick={addGuest}
                                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                                        >
                                            + Add Guest
                                        </button>
                                    </div>
                                    
                                    {joinFormData.additional_guests.length === 0 ? (
                                        <div className="text-gray-500 text-sm italic p-4 border border-gray-200 rounded-lg bg-gray-50">
                                            No additional guests added. Click "Add Guest" to include traveling companions.
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {joinFormData.additional_guests.map((guest, index) => (
                                                <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <h5 className="font-medium text-gray-900">Guest {index + 1}</h5>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeGuest(index)}
                                                            className="text-red-600 hover:text-red-800 text-sm"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Full Name *
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={guest.name}
                                                                onChange={(e) => updateGuest(index, 'name', e.target.value)}
                                                                required
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                placeholder="Guest's full name"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Age (if under 18)
                                                            </label>
                                                            <input
                                                                type="number"
                                                                value={guest.age}
                                                                onChange={(e) => updateGuest(index, 'age', e.target.value)}
                                                                min="0"
                                                                max="17"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                placeholder="Leave blank if 18+"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Package Selection */}
                                {groupBooking.packages && groupBooking.packages.length > 1 && (
                                    <div>
                                        <h4 className="text-lg font-medium text-gray-900 mb-4">Select Your Package *</h4>
                                        <div className="space-y-3">
                                            {groupBooking.packages.map((pkg, index) => (
                                                <label key={index} className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="package_selected"
                                                        value={pkg.name}
                                                        checked={joinFormData.package_selected === pkg.name}
                                                        onChange={handleJoinFormChange}
                                                        className="mt-1"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <h5 className="font-medium text-gray-900">{pkg.name}</h5>
                                                                <p className="text-gray-600 text-sm mt-1">{pkg.description}</p>
                                                            </div>
                                                            <div className="text-right">
                                                                <div className="text-lg font-semibold text-green-600">
                                                                    ${pkg.price}
                                                                </div>
                                                                <div className="text-sm text-gray-500">per person</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Single Package Display */}
                                {groupBooking.packages && groupBooking.packages.length === 1 && (
                                    <div>
                                        <h4 className="text-lg font-medium text-gray-900 mb-4">Package Details</h4>
                                        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h5 className="font-medium text-gray-900">{groupBooking.packages[0].name}</h5>
                                                    <p className="text-gray-600 text-sm mt-1">{groupBooking.packages[0].description}</p>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-lg font-semibold text-green-600">
                                                        ${groupBooking.packages[0].price}
                                                    </div>
                                                    <div className="text-sm text-gray-500">per person</div>
                                                </div>
                                            </div>
                                        </div>
                                        <input type="hidden" name="package_selected" value={groupBooking.packages[0].name} />
                                    </div>
                                )}

                                {/* Special Requests */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Special Requests or Notes
                                    </label>
                                    <textarea
                                        name="special_requests"
                                        value={joinFormData.special_requests}
                                        onChange={handleJoinFormChange}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Any dietary restrictions, accessibility needs, or special requests..."
                                    />
                                </div>

                                {/* Form Actions */}
                                <div className="flex space-x-4 pt-4 border-t">
                                    <button 
                                        type="button"
                                        onClick={() => setShowJoinForm(false)}
                                        className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit"
                                        disabled={joinLoading}
                                        className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                    >
                                        {joinLoading ? 'Processing...' : 'Continue to Payment'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublicGroupBooking;
